import ApiClient from "ApiClient";
import { BONE_MARKER_NAME_PREFIX, BONE_MESH_NAME_PREFIX, DEFAULT_FOOTSHOE_COLOR } from "components/Viewer3d/consts";
import JSZip from "jszip";
import { BoneColors, BoneName } from "models/Bones";
import { InsoleDesign } from "models/InsoleDesign";
import { Mesh, MeshStandardMaterial, Group, Vector3, BufferGeometry, SphereGeometry } from "three";
import * as MyUtils from "Utils/ViewerUtils";
import { InsoleType, ProfileCurveTemplates } from "models/Insole";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import * as WbctUtils from "Utils/WbctUtils";


export async function fetchBonesFile(
  api: ApiClient, designId: number
): Promise<JSZip | null> {
  const res = await api.get(`/api/wbct/${designId}`);
  if (!res.ok) return null;
  const zipArray: Uint8Array = res.body;
  return await JSZip.loadAsync(zipArray);
}

export async function saveBonesFile(api: ApiClient, designId: number, file: any) {
  const formData = new FormData();
  formData.append('designId', designId.toString());
  formData.append('file', file);
  const uploadResult = await api.post('api/wbct', formData);
  if (!uploadResult.ok) {
    console.error(`${uploadResult.status} Failed to upload bones file! ${uploadResult.body}`)
  }
}

export async function GetBones(
  api: any,
  targetSide: "Right" | "Left" | "All" = "All",
  designId: number,
  insoleDesign: InsoleDesign | null,
  applyTempltes: boolean = false
): Promise<void> {

  if (!insoleDesign) {
    console.warn("Bone processing error! Insole Design is null!");
    return;
  }

  const sidesToProcess =
    targetSide === "All" ? ["Right", "Left"] : [targetSide];

  // numeric bone enum values
  const boneNumericValues = Object.values(BoneName)
    .filter((v) => typeof v === "number") as BoneName[];


  const bonesZip = await fetchBonesFile(api, designId);
  if (!bonesZip) {
    console.warn("Bone processing error! Zip variable is null!");
    return;
  }
  for (const side of sidesToProcess) {
    const foot = side === "Right" ? 0 : 1;

    const bones: Mesh[] = [];
    const bonesGroup = new Group();
    bonesGroup.name = `${BONE_MESH_NAME_PREFIX}${foot}`;

    let markers = null;
    const markerPath = `${side}Markers.json`;
    const markerFile = bonesZip.file(markerPath);

    //1. load bonesmarker
    if (!markerFile) {
      console.warn(`Markers file '${markerPath}' not found in zip.`);
    } else {
      const buffer = await markerFile.async("arraybuffer");
      const jsonText = new TextDecoder().decode(buffer);
      markers = createBonesMarkerFromJson(jsonText);
      markers.name = `${BONE_MARKER_NAME_PREFIX}${foot}`
    }
    insoleDesign.insoles[foot].bonesMarkers = markers;

    //2. load bones
    for (const boneKey of boneNumericValues) {
      const boneNameString = BoneName[boneKey];
      const boneColor = BoneColors[boneKey];


      const entryPath = `${side}/${boneNameString}.stl`;
      const geometry = await loadStlFromZip(bonesZip, entryPath);
      if (!geometry) continue;

      const material = new MeshStandardMaterial({
        color: boneColor,
        metalness: 0.5,
        roughness: 0.5,
      });

      const mesh = new Mesh(geometry, material);
      bones.push(mesh);
      bonesGroup.add(mesh);
    }
    insoleDesign.insoles[foot].bonesGroup = bonesGroup;


    //3. load footShoe mesh
    const footPath = `${side}_FootShoe.STL`;
    const geometryFootShoe = await loadStlFromZip(bonesZip, footPath);
    if (!geometryFootShoe) continue;

    const material = new MeshStandardMaterial({
      color: DEFAULT_FOOTSHOE_COLOR, transparent: true, opacity: 0.5, metalness: 0.5, roughness: 0.5, depthWrite: false
    });
    const mesh = new Mesh(geometryFootShoe, material);
    mesh.renderOrder = 1;
    insoleDesign.insoles[foot].footShoeMesh = mesh;

    //4. load FPI markers
    let FPIMarkers = null;
    const FPIMarkerPath = `${side}FPIMarkers.json`;
    const FPIMarkerFile = bonesZip.file(FPIMarkerPath);

    if (!FPIMarkerFile) {
      console.warn(`FPI Markers file '${FPIMarkerPath}' not found in zip.`);
    } else {
      const buffer = await FPIMarkerFile.async("arraybuffer");
      const jsonText = new TextDecoder().decode(buffer);
      FPIMarkers = parseFPIMarkersFromText(jsonText);
    }
    insoleDesign.insoles[foot].bonesFPIMarkers = FPIMarkers;

    const shoeDataXmlPath = `${side}_ObtainShoeData`;
    const shoeDataFile = bonesZip.file(shoeDataXmlPath);

    if (!shoeDataFile) {
      console.warn(`Shoe Data Markers file '${markerPath}' not found in zip.`);
    } else {
      const xmlBuffer = await shoeDataFile.async("arraybuffer");
      const xmlText = new TextDecoder().decode(xmlBuffer);

      const shoeData = parseShoeDataFromXml(xmlText);

      if (shoeData.outsideMarkers.length > 0) {
        insoleDesign.insoles[foot].shoeOutsideMarkers = shoeData.outsideMarkers;
      }

      if (shoeData.insideMarkers.length > 0) {
        insoleDesign.insoles[foot].shoeInsideMarkers = shoeData.insideMarkers;
      }

      if (shoeData.curvePoints.length > 0) {
        insoleDesign.insoles[foot].curvePoints = new Group();
        shoeData.curvePoints.forEach(p =>{
          const geom = new SphereGeometry(2.5);
          const mat = new MeshStandardMaterial({ color: 0x333333 });
          const joint = new Mesh(geom, mat);
          joint.position.copy(new Vector3(p.x, p.y, p.z));
          insoleDesign.insoles[foot].curvePoints.add(joint);
        });
        insoleDesign.insoles[foot].curvePoints.visible = false;
      }
    }
  }


  if (!applyTempltes) return;
  //4. load output. json
  const outputPath = 'output.json';
  const outputFile = bonesZip.file(outputPath);
  if (!outputFile) {
    console.warn(`Markers file '${outputPath}' not found in zip.`);
  } else {
    const buffer = await outputFile.async("arraybuffer");
    const jsonText = new TextDecoder().decode(buffer);
    const raw: Record<string, string> = JSON.parse(jsonText);
    const insoleType = convertINsoleTypeFromWinappToWeb(raw['footType']) as InsoleType;
    const insoleLength = Number.parseInt(raw['footLength']);
    const insoleProfileCurve = convertINsoleTypeFromWinappToWeb(raw['profileCurve']) as ProfileCurveTemplates;

    insoleDesign.autoProperties.insoleTemplate = insoleType;
    insoleDesign.insoles[0].profileCurveType = insoleDesign.insoles[1].profileCurveType = insoleProfileCurve;
    useSelectedDesignStore.setState({ lengthFromWBCT: insoleLength });
  }
}

const loadStlFromZip = async (bonesZip: JSZip, filename: string): Promise<BufferGeometry | null> => {
  const entry = bonesZip.file(filename);
  if (!entry) {
    console.warn(`Could not find STL '${filename}' in ZIP.`);
    return null;
  }
  const stlBuffer = await entry.async("arraybuffer");
  const geometry = MyUtils.loadFromArrayBuffer(stlBuffer);

  if (!geometry) {
    console.warn(`Could not load STL for ${filename}`);
    return null;
  }
  return geometry;
}

const convertINsoleTypeFromWinappToWeb = (name: string) => {
  return name.replace('_', '');
}

export function createBonesMarkerFromJson(
  jsonText: string,
): Group {
  const output: Group = new Group();
  try {
    const raw: Record<string, string> = JSON.parse(jsonText);
    for (const key in raw) {
      if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;

      const numbers = raw[key]
        .split(",")
        .map(n => parseFloat(n.trim()));

      if (numbers.length === 3) {
        const geom = new SphereGeometry(2.5);
        const mat = new MeshStandardMaterial({ color: 0xff0033 });
        const joint = new Mesh(geom, mat);
        joint.name = key;
        joint.position.copy(new Vector3(numbers[0], numbers[1], numbers[2]));
        output.add(joint);
      }
    }
  } catch (error) {
    console.error("Failed to parse markers JSON text:", error);
  }
  return output;
}

export function parseFPIMarkersFromText(
  jsonText: string
): Group {
  var outputEnt = new Group();
  try {
    // 1. Convert the raw string into a JS Object
    const raw: Record<string, string> = JSON.parse(jsonText);
    const output: Record<string, Vector3[]> = {};

    // 2. Loop through the keys and convert "x,y,z" strings to Vector3
    for (const key in raw) {
      if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;

      output[key] = [];
      raw[key].forEach(element => {
        const [x, y, z] = element.split(',').map(n => parseFloat(n.trim()));
        output[key].push(new Vector3(x, y, z));
      });
      if (key === 'TalarHead')
        outputEnt.add(WbctUtils.DrawTalarHead(output[key]));
      if (key === "ArchHeight")
        outputEnt.add(WbctUtils.DrawArchHeight(output[key]));
      if(key === "RearFootAlign")
        outputEnt.add(WbctUtils.DrawRearFootAlign(output[key]));
      if(key === "TransversePlane")
        outputEnt.add(WbctUtils.DrawTransversePlane(output[key]));
      if(key === "CalcaneousPos")
        outputEnt.add(WbctUtils.DrawCalcaneousPos(output[key]));
    }
  } catch (error) {
    console.error("Failed to parse markers JSON text:", error);
  }
  return outputEnt;
}

export function parseShoeDataFromXml(
  xmlText: string
): { outsideMarkers: Vector3[]; insideMarkers: Vector3[]; curvePoints: Vector3[] } {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    const parserError = xmlDoc.querySelector("parsererror");
    if (parserError) {
      console.error("XML parsing error:", parserError.textContent);
      return { outsideMarkers: [], insideMarkers: [], curvePoints: [] };
    }

    const extractPoints = (nodeName: string): Vector3[] => {
      const points: Vector3[] = [];
      const targetNode = xmlDoc.getElementsByTagName(nodeName)[0];

      if (!targetNode) {
        console.warn(`Node '${nodeName}' not found in XML`);
        return points;
      }

      const point3DNodes = targetNode.getElementsByTagName("Point3D");

      for (let i = 0; i < point3DNodes.length; i++) {
        const pointNode = point3DNodes[i];
        const x = parseFloat(pointNode.getElementsByTagName("X")[0]?.textContent || "0");
        const y = parseFloat(pointNode.getElementsByTagName("Y")[0]?.textContent || "0");
        const z = parseFloat(pointNode.getElementsByTagName("Z")[0]?.textContent || "0");

        points.push(new Vector3(x, y, z));
      }

      return points;
    };

    return {
      outsideMarkers: extractPoints("OutsideLine"),
      insideMarkers: extractPoints("InsideLine"),
      curvePoints: extractPoints("LowerCurvePoints")
    };
  } catch (error) {
    console.error("Failed to parse shoe data XML:", error);
    return { outsideMarkers: [], insideMarkers: [], curvePoints: [] };
  }
}

/**
 * Iterates through bone names and loads associated STL files from 'left' or 'right' folders.
 * @param targetSide - 'left', 'right', or 'all' to determine which folders to process.
 */
export async function GetBonesFromFolders(
  targetSide: "Right" | "Left" | "All" = "All",
  insoleDesign: InsoleDesign | null,
): Promise<void> {
  // Get the numeric enum values
  const boneNumericValues = Object.values(BoneName).filter(
    (value) => typeof value === "number",
  ) as BoneName[];

  // Determine which sides to process
  const sidesToProcess: ("Right" | "Left")[] = [];
  if (targetSide === "All") {
    sidesToProcess.push("Right", "Left");
  } else {
    sidesToProcess.push(targetSide);
  }

  let bones: Mesh[] = []

  for (const side of sidesToProcess) {
    bones = [];
    const bonesGroup = new Group();
    const foot = side === "Right" ? 0 : 1;
    // const liftInsoleShift = Math.pow(-1, foot + 1) * 130;

    for (const boneKey of boneNumericValues) {
      const boneNameString = BoneName[boneKey];
      const boneColor = BoneColors[boneKey];
      const filePath = `/data/${side}/${boneNameString}.stl`;
      const geometry = await MyUtils.LoadStl(filePath);

      if (!geometry) {
        console.warn(`Could not load STL for ${boneNameString} (side: ${side}).`);
        continue;
      }
      const material = new MeshStandardMaterial({
        color: boneColor,
        metalness: 0.5,
        roughness: 0.5,
      });
      const mesh = new Mesh(geometry, material);

      bones.push(mesh);
      bonesGroup.add(mesh);
    }

    // const rawMarkers = await fetch(`/data/${side}Markers.json`).then(r => r.json());
    // const markers = parseMarkersJson(rawMarkers, shift);

    if (insoleDesign) {
      const insole = insoleDesign.insoles[foot];
      // insole.bonesMarkers = markers;
      // insole.curvePoints  = CalculateCurveFromBones(markers, false);
      insole.bonesGroup = bonesGroup;
    }
  }

}
